import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, CalendarClock } from "lucide-react";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DOW = [
  { i: 0, l: "L" }, { i: 1, l: "M" }, { i: 2, l: "M" },
  { i: 3, l: "J" }, { i: 4, l: "V" }, { i: 5, l: "S" }, { i: 6, l: "D" },
];
const LEAD_OPTIONS = [15, 20, 30, 45, 60, 90, 120];

const empty = {
  passenger_name: "", passenger_phone: "",
  pickup_address: "", pickup_lat: null, pickup_lng: null, pickup_place_id: null,
  dropoff_address: "", dropoff_lat: null, dropoff_lng: null, dropoff_place_id: null,
  price: "", payment_method: "cash", driver_id: "",
  notes: "", trip_type: "trip", items: "",
  hour: 10, minute: 0,
  weekdays: [],
  start_date: new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" }),
  end_date: "",
  lead_minutes: 15,
  active: true,
};

export default function AppointmentDialog({ open, onOpenChange, appointment, onSaved }) {
  const [form, setForm] = useState(empty);
  const [drivers, setDrivers] = useState([]);
  const [aliases, setAliases] = useState([]);
  const [saving, setSaving] = useState(false);
  const isEdit = !!appointment?.id;
  const isDelivery = form.trip_type === "delivery";

  // Debounced client lookup by phone — mirrors TripFormDialog so the
  // dispatcher doesn't retype the passenger's name for someone already in
  // the ledger. Runs on both create and edit. Autofill NEVER overwrites a
  // manually-typed name (guarded by `!f.passenger_name`).
  useEffect(() => {
    if (!open) return;
    const phone = (form.passenger_phone || "").trim();
    if (phone.length < 6) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients/lookup`, { params: { phone } });
        setForm((f) => (
          !f.passenger_name && data?.passenger_name
            ? { ...f, passenger_name: data.passenger_name }
            : f
        ));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.debug("appt client lookup failed:", err?.message || err);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [open, form.passenger_phone]);

  useEffect(() => {
    if (!open) return;
    api.get("/users/drivers").then(({ data }) => setDrivers(data || [])).catch(() => {});
    api.get("/aliases").then(({ data }) => setAliases(data || [])).catch(() => {});
    if (appointment) {
      setForm({
        passenger_name: appointment.passenger_name || "",
        passenger_phone: appointment.passenger_phone || "",
        pickup_address: appointment.pickup_address || "",
        pickup_lat: appointment.pickup_lat ?? null,
        pickup_lng: appointment.pickup_lng ?? null,
        pickup_place_id: appointment.pickup_place_id || null,
        dropoff_address: appointment.dropoff_address || "",
        dropoff_lat: appointment.dropoff_lat ?? null,
        dropoff_lng: appointment.dropoff_lng ?? null,
        dropoff_place_id: appointment.dropoff_place_id || null,
        price: appointment.price ?? "",
        payment_method: appointment.payment_method || "cash",
        driver_id: appointment.driver_id || "",
        notes: appointment.notes || "",
        trip_type: appointment.trip_type || "trip",
        items: appointment.items || "",
        hour: appointment.hour ?? 10,
        minute: appointment.minute ?? 0,
        weekdays: appointment.weekdays || [],
        start_date: appointment.start_date || empty.start_date,
        end_date: appointment.end_date || "",
        lead_minutes: appointment.lead_minutes || 15,
        active: appointment.active !== false,
      });
    } else {
      setForm({ ...empty, start_date: empty.start_date });
    }
  }, [appointment, open]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const toggleWeekday = useCallback((i) => setForm((f) => {
    const has = f.weekdays.includes(i);
    return { ...f, weekdays: has ? f.weekdays.filter((x) => x !== i) : [...f.weekdays, i].sort((a, b) => a - b) };
  }), []);

  const swapAddresses = () => setForm((f) => ({
    ...f,
    pickup_address: f.dropoff_address, pickup_lat: f.dropoff_lat, pickup_lng: f.dropoff_lng, pickup_place_id: f.dropoff_place_id,
    dropoff_address: f.pickup_address, dropoff_lat: f.pickup_lat, dropoff_lng: f.pickup_lng, dropoff_place_id: f.pickup_place_id,
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date) { toast.error("Selecciona la fecha de inicio"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price || 0),
        hour: Number(form.hour), minute: Number(form.minute),
        lead_minutes: Number(form.lead_minutes),
        driver_id: form.driver_id || null,
      };
      if (isEdit) {
        await api.patch(`/appointments/${appointment.id}`, payload);
        toast.success("Cita actualizada");
      } else {
        await api.post("/appointments", payload);
        toast.success("Cita creada. Bajará automáticamente al panel.");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="appointment-dialog"
        className="max-w-2xl bg-card border-border p-0 max-h-[90vh] flex flex-col"
      >
        <DialogHeader className="p-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2 text-white">
            <CalendarClock className="w-5 h-5 text-primary" /> {isEdit ? "Editar cita" : "Nueva cita"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Trip type */}
          <div className="grid grid-cols-2 gap-2 text-xs font-bold">
            <button type="button" onClick={() => setForm((f) => ({ ...f, trip_type: "trip" }))}
              className={`py-2 px-3 rounded-md border transition-all ${!isDelivery ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-white"}`}
              data-testid="appt-type-trip">Viaje</button>
            <button type="button" onClick={() => setForm((f) => ({ ...f, trip_type: "delivery" }))}
              className={`py-2 px-3 rounded-md border transition-all ${isDelivery ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:text-white"}`}
              data-testid="appt-type-delivery">Delivery</button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Pasajero</Label>
              <Input data-testid="appt-passenger-name" value={form.passenger_name} onChange={upd("passenger_name")} />
            </div>
            <div className="space-y-1">
              <Label>Teléfono</Label>
              <Input data-testid="appt-passenger-phone" value={form.passenger_phone} onChange={upd("passenger_phone")} placeholder="+1 555 000 0000" />
            </div>
          </div>

          <div className="space-y-1">
            <Label>{isDelivery ? "Restaurante / Pickup" : "Pickup (dirección)"}</Label>
            <PlaceAutocomplete
              value={form.pickup_address}
              onChange={(v) => setForm((f) => ({ ...f, pickup_address: v }))}
              onPlace={({ address, lat, lng, place_id }) => setForm((f) => ({ ...f, pickup_address: address, pickup_lat: lat, pickup_lng: lng, pickup_place_id: place_id || null }))}
              testId="appt-pickup-address"
              className="bg-input border-border"
              aliases={aliases}
            />
          </div>

          <div className="flex justify-end -my-1">
            <button type="button" onClick={swapAddresses} title="Intercambiar"
              data-testid="appt-swap-addresses"
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-input/60 text-muted-foreground hover:text-primary hover:border-primary transition-all">
              <ArrowUpDown className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-1">
            <Label>Drop off (dirección)</Label>
            <PlaceAutocomplete
              value={form.dropoff_address}
              onChange={(v) => setForm((f) => ({ ...f, dropoff_address: v }))}
              onPlace={({ address, lat, lng, place_id }) => setForm((f) => ({ ...f, dropoff_address: address, dropoff_lat: lat, dropoff_lng: lng, dropoff_place_id: place_id || null }))}
              testId="appt-dropoff-address"
              className="bg-input border-border"
              aliases={aliases}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Precio (USD)</Label>
              <Input data-testid="appt-price" type="number" step="0.01" value={form.price} onChange={upd("price")} />
            </div>
            <div className="space-y-1">
              <Label>Hora <span className="text-[10px] font-mono uppercase tracking-widest text-primary/80">(Naples EDT)</span></Label>
              <div className="flex gap-1">
                <Input data-testid="appt-hour" type="number" min="0" max="23" value={form.hour} onChange={upd("hour")} className="text-center" />
                <span className="text-white/80 self-center">:</span>
                <Input data-testid="appt-minute" type="number" min="0" max="59" value={form.minute} onChange={upd("minute")} className="text-center" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Método pago</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}>
                <SelectTrigger data-testid="appt-payment" className="bg-input border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="credito">Crédito</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Recurrence weekdays */}
          <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
            <Label className="text-primary">Días a repetir (todos vacíos = todos los días del rango, o una sola cita si no pones "Hasta")</Label>
            <div className="flex gap-1.5 flex-wrap">
              {DOW.map(({ i, l }) => {
                const on = form.weekdays.includes(i);
                return (
                  <button
                    key={i} type="button"
                    data-testid={`appt-dow-${i}`}
                    onClick={() => toggleWeekday(i)}
                    className={`h-9 w-9 rounded-md border font-bold text-xs transition-all ${on ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50 hover:text-white"}`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Desde <span className="text-[9px] font-mono uppercase tracking-widest text-primary/80">(Naples)</span></Label>
                <Input data-testid="appt-start-date" type="date" value={form.start_date} onChange={upd("start_date")} className="bg-input border-border" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hasta (opcional)</Label>
                <Input data-testid="appt-end-date" type="date" value={form.end_date} onChange={upd("end_date")} className="bg-input border-border" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Bajar al panel</Label>
              <Select value={String(form.lead_minutes)} onValueChange={(v) => setForm((f) => ({ ...f, lead_minutes: Number(v) }))}>
                <SelectTrigger data-testid="appt-lead" className="bg-input border-border"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAD_OPTIONS.map((n) => <SelectItem key={n} value={String(n)}>{n} min antes</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Chofer preferido (opcional)</Label>
              <Select value={form.driver_id || "none"} onValueChange={(v) => setForm((f) => ({ ...f, driver_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="appt-driver" className="bg-input border-border"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin asignar</SelectItem>
                  {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>{isDelivery ? "Productos / items" : "Notas"}</Label>
            <Textarea
              data-testid="appt-notes"
              value={isDelivery ? form.items : form.notes}
              onChange={(e) => setForm((f) => ({ ...f, [isDelivery ? "items" : "notes"]: e.target.value.toUpperCase() }))}
              rows={2}
              className="uppercase"
              placeholder={isDelivery ? "Ej: 2 PIZZAS, 1 REFRESCO…" : ""}
            />
          </div>
        </form>

        <DialogFooter className="p-4 border-t border-border shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="appt-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold" data-testid="appt-save">
            {saving ? "Guardando…" : (isEdit ? "Guardar" : "Crear cita")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
